/**
 * A previsão como continuação da linha do tempo: uma zona tracejada em ciano à
 * direita do eixo das imagens, como o futuro no Windy e no RainViewer. Antes a
 * previsão só se ligava no painel do rastreio, e quem não o abria não sabia
 * que ela existia.
 *
 * - Está sempre à vista (com o rótulo "Previsão" e o selo de novidade), sem
 *   pedir nada à máquina: o arquivo de previsão só baixa com um toque nela.
 * - Um toque ou arrasto leva à imagem mais nova que tem previsão e põe na
 *   tela a antecedência mais próxima do ponto tocado (`verPrevisao`).
 * - O cursor ciano anda aqui; no eixo das imagens fica a de origem, vazada.
 * - Desktop: marcas de cada antecedência, "+1 h · +2 h · +3 h" embaixo e um
 *   ▶ próprio para o fluxo. Celular: 56 px, alvo de 44 px de altura, sem ▶ (o
 *   fluxo continua na folha); embaixo, só a antecedência na tela.
 * - Some sem a máquina de rastreio, com o satélite desligado e no período
 *   personalizado (a verificação pelo painel continua).
 *
 * O teclado e as regras de entrar e sair ficam em `fluxo.ts`
 * (`teclaDaPrevisao`, `sairDaPrevisao`); as contas, em `zona.ts`.
 */
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import { formatTimeBrt, MINUTE_MS } from '../lib/time';
import { useExportLock } from '../loop/playback';
import { useLoopStore } from '../loop/store';
import { useGoesStore } from '../state/goes';
import { useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { useEhNovidade } from '../ui/novidade';
import { BASE_RASTREIO } from './api';
import { tocarPrevisao, teclaDaPrevisao, verPrevisao } from './fluxo';
import { antecedenciaEscolhida, antecedenciaNaTela, previsaoDesenhada, useTrackingStore } from './store';
import { antecedenciasNoQuadro, quadroMaisNovoComPrevisao, rotuloAntecedencia } from './tempo';
import {
  agoraNaZona,
  ANTECEDENCIAS_PADRAO,
  antecedenciaNaPosicao,
  estaNaZona,
  NOVIDADE_DA_ZONA,
  posicaoNaZona,
  rotulosDaZona,
  textoDaZona,
} from './zona';

const pct = (p: number) => `${(p * 100).toFixed(3)}%`;

/** A zona está na barra: máquina de rastreio configurada, satélite ligado e período que não é o personalizado. */
export function useZonaNaBarra(): boolean {
  const satelite = useGoesStore((s) => s.layers.some((l) => l.visible));
  const custom = useLoopStore((s) => s.period === 'custom');
  // Só com a Previsão ligada (pedido do usuário, 08/10/2026): desligada, a barra é só a observação.
  const previsao = useTrackingStore((s) => s.previsao);
  return previsao && !!BASE_RASTREIO && satelite && !custom;
}

/** A antecedência com contorno previsto desenhado no mapa (0 = nenhum): o relógio ciano, a moldura e o aviso. */
export function usePrevisaoDesenhada(): number {
  const satelite = useGoesStore((s) => s.layers.some((l) => l.visible));
  return useTrackingStore((s) => (BASE_RASTREIO ? previsaoDesenhada(s, satelite) : 0));
}

/** O índice (em `frames`) da imagem de onde a zona parte, ou −1. */
function useOrigemDaZona(): number {
  const frames = useTimeStore((s) => s.frames);
  const indice = useTrackingStore((s) => s.indice);
  return useMemo(() => (indice ? quadroMaisNovoComPrevisao(frames, indice.quadros) : -1), [frames, indice]);
}

/** O cursor do tempo está na zona (o eixo das imagens mostra a origem vazada). */
export function useCursorNaZona(): boolean {
  const visivel = useZonaNaBarra();
  const origem = useOrigemDaZona();
  const index = useTimeStore((s) => s.index);
  const previsao = useTrackingStore((s) => s.previsao);
  const naTela = useTrackingStore((s) => antecedenciaNaTela(s));
  return visivel && estaNaZona(previsao, naTela, index, origem);
}

/** Largura de um elemento, acompanhada (para os rótulos não se encostarem). `ativo`: o elemento está montado. */
function useLargura(ref: RefObject<HTMLElement | null>, ativo: boolean): number {
  const [largura, setLargura] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || !ativo) return;
    const medir = () => setLargura(el.getBoundingClientRect().width);
    medir();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [ref, ativo]);
  return largura;
}

/** Espaço (px) do rótulo "Previsão" com o selo, à esquerda, e meia largura de um rótulo "+2 h". */
const LARGURA_DO_NOME = 56;
const MEIO_ROTULO = 14;

export function ZonaDaPrevisao(props: { compact?: boolean }) {
  const compact = !!props.compact;
  const visivel = useZonaNaBarra();
  const travada = useExportLock();
  const frames = useTimeStore((s) => s.frames);
  const index = useTimeStore((s) => s.index);
  const indice = useTrackingStore((s) => s.indice);
  const previsao = useTrackingStore((s) => s.previsao);
  const fluxo = useTrackingStore((s) => s.fluxo);
  const naTela = useTrackingStore((s) => antecedenciaNaTela(s));
  const escolhida = useTrackingStore((s) => antecedenciaEscolhida(s));
  const hNoQuadro = useTrackingStore((s) => (s.noQuadro.previsao === 'ok' ? s.hNoQuadro : null));
  const novidade = useEhNovidade(NOVIDADE_DA_ZONA.id, NOVIDADE_DA_ZONA.ate);
  const now = useNow(30_000);
  const origem = useOrigemDaZona();
  const trilho = useRef<HTMLDivElement>(null);
  const linhaDosRotulos = useRef<HTMLDivElement>(null);
  const larguraDosRotulos = useLargura(linhaDosRotulos, visivel && !compact);
  const arrastando = useRef(false);
  const ultimoPedido = useRef<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  // O anel de foco só para quem chega pelo teclado: depois de um toque ele parecia uma moldura da zona.
  const peloPonteiro = useRef(false);
  const [anel, setAnel] = useState(false);

  const origemT = origem >= 0 ? (frames[origem] ?? null) : null;
  const naZona = estaNaZona(previsao, naTela, index, origem);
  // O fluxo tocando a partir da origem é da zona, também no passo 0 (a observação, no eixo das imagens).
  const tocando = previsao && fluxo && origem >= 0 && index === origem;
  // O arquivo na tela só vale para a zona quando é o da origem (arquivos antigos têm outro passo).
  const { passos, disponiveis } = antecedenciasNoQuadro(indice?.antecedencias ?? ANTECEDENCIAS_PADRAO, origem >= 0 && index === origem ? hNoQuadro : null);
  const maxH = passos[passos.length - 1] ?? ANTECEDENCIAS_PADRAO[ANTECEDENCIAS_PADRAO.length - 1];
  // O índice chegou e nenhuma imagem do loop tem previsão. Antes dele a zona aceita o pedido, que fica pendente.
  const semPrevisao = !!indice && origem < 0;
  const desabilitada = semPrevisao || travada;
  const agora = agoraNaZona(now, origemT, maxH);

  if (!visivel) return null;

  const posicaoDoEvento = (e: PointerEvent): number => {
    const r = trilho.current!.getBoundingClientRect();
    return (e.clientX - r.left) / Math.max(1, r.width);
  };
  const pedir = (e: PointerEvent) => {
    const h = antecedenciaNaPosicao(posicaoDoEvento(e), disponiveis, maxH);
    if (h <= 0 || h === ultimoPedido.current) return;
    ultimoPedido.current = h;
    verPrevisao(h, 'mais-nova');
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (desabilitada || e.button !== 0) return;
    arrastando.current = true;
    ultimoPedido.current = null;
    peloPonteiro.current = true;
    setAnel(false);
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.focus({ preventScroll: true });
    setHover(null);
    pedir(e);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (arrastando.current) pedir(e);
    else if (e.pointerType === 'mouse' && !desabilitada) setHover(antecedenciaNaPosicao(posicaoDoEvento(e), disponiveis, maxH));
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!arrastando.current) return;
    arrastando.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (desabilitada) return;
    if (teclaDaPrevisao(e.key)) {
      e.preventDefault();
      return;
    }
    // Fora da zona (previsão desligada, ou ligada noutra imagem): as teclas entram nela.
    let alvo: number | undefined;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'Home') alvo = disponiveis[0];
    else if (e.key === 'End' || e.key === 'PageUp') alvo = disponiveis[disponiveis.length - 1];
    else if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      tocarPrevisao();
      return;
    }
    if (alvo === undefined) return;
    e.preventDefault();
    verPrevisao(alvo, 'mais-nova');
  };

  // Desenho: o trilho tracejado vai de ponta a ponta; marcas e cursores ficam 6 px para dentro,
  // para o cursor de +3 h não invadir o ▶ e o de +30 não encostar no eixo das imagens.
  const trackTop = compact ? 'top-[12px]' : 'top-[9px]';
  const rotulos = rotulosDaZona(passos, compact, naZona ? naTela : 0);
  const balao = hover !== null && !arrastando.current && hover > 0;
  const marcadorVazado = previsao && !naZona && !tocando;

  return (
    <div
      data-zona-caixa
      className="flex-none"
      // Celular: 56 px fixos, o bastante para um alvo de 44 px de altura com "Previsão" embaixo.
      style={{ width: compact ? 56 : 'clamp(104px, 20%, 200px)' }}
    >
      <div className="flex items-center gap-1">
        <div
          role="slider"
          data-zona-previsao
          tabIndex={desabilitada ? -1 : 0}
          aria-label="Previsão das nuvens"
          aria-valuemin={0}
          aria-valuemax={maxH}
          aria-valuenow={naZona ? naTela : 0}
          aria-valuetext={semPrevisao ? 'Sem previsão neste período' : textoDaZona(naZona ? naTela : 0, origemT)}
          aria-disabled={desabilitada || undefined}
          aria-orientation="horizontal"
          title={semPrevisao ? 'Sem previsão neste período' : undefined}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={() => setHover(null)}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setAnel(!peloPonteiro.current);
            peloPonteiro.current = false;
          }}
          onBlur={() => setAnel(false)}
          className={clsx(
            'relative min-w-0 flex-1 touch-none rounded-md outline-none select-none',
            anel && 'focus-visible:ring-2 focus-visible:ring-previsao',
            compact ? 'h-11' : 'h-[22px]',
            desabilitada ? 'cursor-default opacity-50' : 'cursor-pointer',
          )}
        >
          <div className={clsx('zona-previsao absolute inset-x-0 h-1 rounded-full', trackTop)} aria-hidden />
          <div ref={trilho} className="absolute inset-y-0 right-1.5 left-1.5">
            {/* uma marca por antecedência; as que a imagem de origem não tem ficam apagadas */}
            {passos.map((h) => (
              <span
                key={h}
                className={clsx('absolute h-1 w-px', compact ? 'top-[18px]' : 'top-[15px]', disponiveis.includes(h) ? 'bg-previsao/70' : 'bg-ink-500')}
                style={{ left: pct(posicaoNaZona(h, maxH)) }}
                aria-hidden
              />
            ))}
            {/* agora: a previsão parte de uma imagem de 10–25 min atrás */}
            {agora !== null && (
              <span
                className={clsx('absolute w-px bg-ink-100', compact ? 'top-[4px] h-5' : 'top-[3px] h-4')}
                style={{ left: pct(agora) }}
                role="img"
                aria-label={`Agora: ${formatTimeBrt(now)} (Brasília)`}
                title={`Agora: ${formatTimeBrt(now)} (Brasília).`}
              >
                <span className="absolute -top-1 -left-[3px] border-x-[3.5px] border-t-[4px] border-x-transparent border-t-ink-100" aria-hidden />
              </span>
            )}
            {/* o cursor do tempo, quando está aqui */}
            {naZona && (
              <span
                data-cursor-zona
                className={clsx(
                  'absolute size-4 -translate-x-1/2 rounded-full border-2 border-ink-900 bg-previsao shadow-[0_0_0_1px_var(--color-previsao)]',
                  compact ? 'top-[6px]' : 'top-[3px]',
                )}
                style={{ left: pct(posicaoNaZona(naTela, maxH)) }}
                aria-hidden
              />
            )}
            {/* previsão ligada noutra imagem (link, preferências, verificação): onde um toque leva */}
            {marcadorVazado && (
              <span
                className={clsx('absolute size-3 -translate-x-1/2 rounded-full border-2 border-previsao bg-ink-900', compact ? 'top-[8px]' : 'top-[5px]')}
                style={{ left: pct(posicaoNaZona(escolhida, maxH)) }}
                aria-hidden
              />
            )}
            {balao && (
              <span
                className="pointer-events-none absolute -top-6 z-10 -translate-x-1/2 rounded-md border border-ink-600 bg-ink-900 px-1.5 py-0.5 font-mono text-[10px] whitespace-nowrap text-previsao tabular-nums"
                style={{ left: pct(posicaoNaZona(hover, maxH)) }}
                aria-hidden
              >
                {rotuloAntecedencia(hover)}
                {origemT !== null && ` · ${formatTimeBrt(origemT + hover * MINUTE_MS)}`}
              </span>
            )}
          </div>
          {compact && (
            // Celular: dentro do alvo de 44 px, como os rótulos do eixo das imagens.
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-4 items-center justify-center font-sans text-[10px] leading-4 whitespace-nowrap tabular-nums">
              {rotulos.length ? (
                <span className="font-semibold text-previsao">{rotulos[0].texto}</span>
              ) : (
                <span className={desabilitada ? 'text-ink-400' : 'text-previsao/90'}>Previsão</span>
              )}
            </div>
          )}
          {compact && novidade && !desabilitada && <Selo className="absolute top-0.5 right-0.5" />}
        </div>
        {/* O ▶ que a zona tinha saiu (08/10/2026): o botão da previsão, ao lado, liga e já toca. */}
      </div>
      {!compact && (
        <>
          {/* a altura da linha do download, sob o eixo das imagens */}
          <div className="h-1" aria-hidden />
          <div className="mt-0.5 flex h-4 gap-1">
            <div ref={linhaDosRotulos} className="relative min-w-0 flex-1 font-mono text-[10px] leading-4 whitespace-nowrap tabular-nums">
              <span className={clsx('absolute left-0 flex items-center gap-1 font-sans', desabilitada ? 'text-ink-400' : 'text-previsao/90')}>
                Previsão
                {novidade && !desabilitada && <Selo />}
              </span>
              {rotulos.map(({ h, texto }, i) => {
                const ultimo = i === rotulos.length - 1;
                // 6 px de margem de cada lado, como as marcas.
                const x = 6 + posicaoNaZona(h, maxH) * Math.max(0, larguraDosRotulos - 12);
                // O último fica encostado à direita; os do meio, só se não encostam no nome nem nele.
                if (!ultimo && (x - MEIO_ROTULO < LARGURA_DO_NOME || x + MEIO_ROTULO > larguraDosRotulos - 2 * MEIO_ROTULO - 4)) return null;
                return (
                  <span
                    key={h}
                    className={clsx('absolute', naZona && h === naTela ? 'font-semibold text-white' : 'text-ink-300', ultimo ? 'right-0' : '-translate-x-1/2')}
                    style={ultimo ? undefined : { left: `${x}px` }}
                  >
                    {texto}
                  </span>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** O selo de novidade (o mesmo ponto verde do trilho de painéis): some no primeiro uso da zona. */
function Selo(props: { className?: string }) {
  return (
    <span
      className={clsx('size-1.5 shrink-0 rounded-full bg-mata-400 ring-2 ring-ink-900', props.className)}
      role="img"
      aria-label="novidade"
      title="Novidade: a previsão agora fica na linha do tempo"
    />
  );
}
